<template>
  <div class="qa-rubric" data-test="qa-rubric">
    <!-- Audit form: one row per question with Yes / No / N/A. -->
    <template v-if="mode === 'answer'">
      <div v-for="q in questions" :key="q.id" style="padding: 10px 0; border-top: 1px solid #F0F0F1;" data-test="qa-rubric-question">
        <div style="color: #353537; margin-bottom: 7px;">{{ q.question }}</div>
        <div style="display: flex; gap: 6px;">
          <button
            v-for="answer in ANSWERS"
            :key="answer"
            type="button"
            class="qa-back"
            :style="answerFor(q.id) === answer ? { borderColor: '#F76734', color: '#F76734', background: '#FEF3F0' } : {}"
            :data-test="`qa-rubric-answer-${q.id}-${answer}`"
            @click="setAnswer(q.id, answer)"
          >{{ answer }}</button>
        </div>
      </div>
    </template>

    <!-- A graded call (the QA report's call detail) and the rubric preview (template editor) share one table. -->
    <template v-else>
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr v-if="mode === 'graded'">
            <th class="qa-th" style="width: 66px;">Answer</th>
            <th class="qa-th">Question</th>
            <th class="qa-th" style="width: 42%;">Evidence from the transcript</th>
          </tr>
          <tr v-else>
            <th class="qa-th" style="width: 18%;">Section</th>
            <th class="qa-th">Question</th>
            <th class="qa-th" style="width: 30%;">Scoring</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            style="border-top: 1px solid #F0F0F1; vertical-align: top;"
            :style="row.autoFailed ? { background: '#FEF6F6' } : null"
            data-test="qa-rubric-question"
          >
            <template v-if="mode === 'graded'">
              <td class="qa-td" style="padding-top: 9px;">
                <span class="qa-tag" :style="answerStyle(row.answer)" data-test="qa-rubric-answer">{{ row.answer }}</span>
              </td>
              <td class="qa-td" style="padding-top: 9px; padding-bottom: 9px; line-height: 1.45;">
                {{ row.question }}
                <span v-if="row.autoFailed" style="margin-left: 5px; font-weight: 700; color: #B91C1C; letter-spacing: .03em;">AUTO-FAIL</span>
                <div v-if="row.rationale" style="color: #A5A5A6; margin-top: 3px; line-height: 1.45;">{{ row.rationale }}</div>
              </td>
              <td class="qa-td" style="padding-top: 9px; padding-bottom: 9px;">
                <!-- The quote is the whole point of the level: a grade a manager can check against what was said. -->
                <div v-if="row.quote" style="border-left: 2px solid #E8E9E9; padding-left: 9px; color: #6B7280; font-style: italic; line-height: 1.5;">“{{ row.quote }}”</div>
                <span v-else style="color: #D4D4D6;">no quote captured</span>
              </td>
            </template>
            <template v-else>
              <td class="qa-td" style="padding-top: 9px; color: #6B7280;" data-test="qa-rubric-section">{{ row.section || '—' }}</td>
              <td class="qa-td" style="padding-top: 9px; padding-bottom: 9px; line-height: 1.45;">
                {{ row.question }}
                <div style="color: #A5A5A6; margin-top: 3px; font-family: ui-monospace, monospace; font-size: 0.86em;">{{ row.id }}</div>
              </td>
              <td class="qa-td" style="padding-top: 9px;" data-test="qa-rubric-tags">
                <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                  <span v-if="row.scored" class="qa-tag">{{ row.points }} {{ row.points === 1 ? 'point' : 'points' }}</span>
                  <span v-else class="qa-tag qa-tag-warn">Info only</span>
                  <span v-if="row.auto_fail" class="qa-tag qa-tag-fail">Auto-fail if No</span>
                  <span v-if="!row.allow_na" class="qa-tag">N/A not allowed</span>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </template>

    <div v-if="!rows.length" style="padding: 28px; text-align: center; color: #A5A5A6;">{{ emptyText }}</div>
  </div>
</template>

<script>
const ANSWERS = ['Yes', 'No', 'N/A'];

// One QA rubric, drawn the same way everywhere: the QA report's call detail (graded), the audit form (answer) and
// the template editor's Display tab (preview). Markup and styles moved here from CareManagement_QA.vue.
// questions: [{id, question, section, points, scored, allow_na, auto_fail}] (the rubrics endpoint, or qaQuestions() in qaRubricData.js).
// answers:   [{id, answer, quote, rationale}] (answer and graded modes).
export default {
  name: 'QaRubric',
  props: {
    questions: { type: Array, default: () => [] },
    answers: { type: Array, default: () => [] },
    mode: { type: String, default: 'preview', validator: (v) => ['preview', 'answer', 'graded'].includes(v) },
    autoFailIds: { type: Array, default: () => [] },
    emptyText: { type: String, default: 'This rubric has no questions.' },
  },
  emits: ['update:answers'],
  data() {
    return { ANSWERS };
  },
  computed: {
    rows() {
      const autoFails = new Set(this.autoFailIds);
      if (this.mode !== 'graded') return this.questions.map((q) => ({ ...q, autoFailed: false }));
      // A graded call lists its answers in the order given; question text comes from the rubric, or the answer itself
      // for a question the rubric no longer lists.
      const byId = Object.fromEntries(this.questions.map((q) => [q.id, q]));
      return this.answers.map((a) => ({ ...a, question: byId[a.id]?.question || a.question || a.id, autoFailed: autoFails.has(a.id) }));
    },
  },
  methods: {
    answerFor(id) {
      return (this.answers.find((a) => a.id === id) || {}).answer;
    },
    setAnswer(id, answer) {
      const current = this.questions.map((q) => this.answers.find((a) => a.id === q.id) || { id: q.id, answer: '', quote: '', rationale: '' });
      this.$emit('update:answers', current.map((a) => (a.id === id ? { ...a, answer } : a)));
    },
    answerStyle(a) {
      if (a === 'Yes') return { background: '#ECFDF5', color: '#0E6B43', borderColor: '#A7F3D0' };
      if (a === 'No') return { background: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' };
      return { background: '#F8F8F9', color: '#A5A5A6', borderColor: '#E8E9E9' };
    },
  },
};
</script>

<style scoped>
/* Moved from CareManagement_QA.vue (which keeps its own copy for its other tables until the QA page restyle). */
.qa-rubric { font-size: 14px; }
.qa-rubric button { font-family: inherit; }

.qa-back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border: 1px solid #E8E9E9;
  border-radius: 4px;
  background: #FFFFFF;
  color: #6B7280;
  font-size: 0.86em;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.qa-back:hover { border-color: #0284C7; color: #0369A1; background: #F8FAFC; }

.qa-th {
  background: #FFFFFF;
  border-bottom: 1px solid #E8E9E9;
  text-align: left;
  padding: 7px 10px;
  font-size: 0.86em;
  font-weight: 600;
  color: #6B7280;
  white-space: nowrap;
}
.qa-td {
  padding: 7px 10px;
  color: #353537;
}

/* Small status pills: answers, auto-fail, info only. */
.qa-tag {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #E8E9E9;
  border-radius: 9px;
  font-size: 0.86em;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
}
.qa-tag-warn { background: #FFFBEB; color: #B45309; border-color: #FDE68A; }
.qa-tag-fail { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; }
</style>
